import React, {Component, Fragment} from 'react';
import {CircularProgress, CssBaseline, ThemeProvider} from "@mui/material"
import ThemeCustomization from './components/Theme/Theme';
import Welcome from './views/Welcome';
import FormPages from './views/FormPages';
import Review from './views/Review';
import ThankYou from './views/ThankYou';
import Error from "./views/Error";
import {PageView} from "./model/enums/PageView";
import {AppState} from "./model/AppState";
import {App} from "./model/App";
import moment from "moment";
import {LocalStorageUtil} from "./util/LocalStorageUtil";
import {SignatureUtil} from "./util/SignatureUtil";
import {AppContext} from "./context/AppContext";
import {HistoryStateUtils} from "./util/HistoryStateUtils";
import { Helmet } from 'react-helmet';
import axios from "axios";
import { ERROR_MESSAGES } from './constants/errorMessages';
import { setupCsrfToken } from './axiosSetup';

class PowformApp extends Component<{}, AppState> {

    theme: any;

    constructor(props: any) {
        super(props);
        this.state = new AppState(0, new App(), {}, PageView.BeforeLoaded);
    }

    private getTheme() {
       if (this.state.app?.template?.colors &&
           this.state.app.template.colors.primary !== '' &&
           this.state.app.template.colors.accent !== '') {
           return ThemeCustomization(this.state.app.template.colors.primary, this.state.app.template.colors.accent);
        }
        return ThemeCustomization("#3f51b5", "#f50057");
    }

    async componentDidMount() {
        const path = window.location.pathname;

        let app;
        const version = process.env.REACT_APP_VERSION || '1.0.0'; // App version

        let uri = `/api/webapp/app${path}`;


        PowformApp.setBrowserLocale();
        await setupCsrfToken();

        try {
            const response = await axios.post(uri);
            app = response.data.app;
        } catch(e: any) {

        }

        if (app !== undefined) {
            document.title = app.name;
            this.theme = ThemeCustomization(app.template.colors.primary, app.template.colors.accent);

            if (app.schemas === undefined && app.messages.length) {
                this.setState({pageView: PageView.Error, message: app.messages[0].text, app: app, showAppInfo: true});
            } else {
                SignatureUtil.clearAllPageSignatures(this.state.appData, app.schemas.uiSchema,
                    app.schemas.dataSchema);
                if(app.autoOpenOnFormPage !== undefined){
                    SignatureUtil.savePageSignatures(app.autoOpenOnFormPage, app.schemas.data,
                        app.schemas.uiSchema, app.schemas.dataSchema);
                }else{
                    HistoryStateUtils.removeSavedStateFromUrlPath();
                }
                let newAppState = new AppState(app.autoOpenOnFormPage ?
                        app.autoOpenOnFormPage :
                        0, app, app.schemas.data,
                    (app.config?.hideIntroPage || app.autoOpenOnFormPage !== undefined) ?
                        PageView.FormPages :
                        PageView.Welcome
                );
                if (app.payment?.config && app.payment.provider === 'paypal') {
                    this.loadPaypalSdkScript(app.payment.config, newAppState);
                } else if (app.payment?.config && app.payment.provider === 'stripe') {
                    const { loadStripe } = await import('@stripe/stripe-js');
                    const stripePromise = loadStripe(app.payment.config.publicKey);
                    let errorMessage = app.payment.config.messages.paymentConfigErrorMessage;

                    stripePromise
                        .then(() => {
                            newAppState.app.payment.config.stripePromise = stripePromise;
                            this.setState(newAppState);
                        })
                        .catch(() => {
                            this.setState({ pageView: PageView.Error, message: errorMessage });
                        });
                } else {
                    this.setState(newAppState);
                }
            }

        } else {
            this.setState({pageView: PageView.Error, message: ERROR_MESSAGES.REQUESTED_APP_NOT_AVAILABLE, app: new App()});
        }
    }

    componentDidUpdate(_: unknown, prevState: AppState) {
        if (prevState.app.logo !== this.state.app.logo && this.state.app.logo) {
            const favicon1 = document.getElementById('favicon1') as HTMLLinkElement | null;
            const favicon2 = document.getElementById('favicon2') as HTMLLinkElement | null;
            favicon1 && (favicon1.href = this.state.app.logo);
            favicon2 && (favicon2.href = this.state.app.logo);
        }
    }

    handleShowSpecificPage = (pageView: PageView) => {
        this.setState({ pageView: pageView });
    }

    handleShowSpecificPageWithDataClick = (appData: any, page: number, view: PageView) => {
        this.setState({ appData: appData, pageView: view, page: page });
    }

    handleShowErrorPage = (message: string, allowBack: boolean, appData: any,
                           messageItems: Array<string> = [], actionMessage: string = '') => {
        this.setState({
            pageView: PageView.Error,
            message: message,
            allowBack: allowBack,
            appData: appData,
            messageItems: messageItems,
            actionMessage: actionMessage,
            showAppInfo: true
        });
    }

    handleSuccessSubmitFormData = (appData: any, uniqueIds: Array<{key: string, value: string}>,
                                   paymentDetails: Array<{key: string, value: string}>, hasSuccessPurchase: boolean) => {
        LocalStorageUtil.clearStorage();
        this.setState({ appData: appData, pageView: PageView.ThankYou, uniqueIds: uniqueIds,
            paymentDetails: paymentDetails, hasSuccessPurchase: hasSuccessPurchase });
    }

    render() {
        if (this.state.pageView === PageView.BeforeLoaded) {
            return (
                <Fragment>
                    <div className="centered-box">
                        <CircularProgress color="primary" size={60} />
                    </div>
                </Fragment>
            );
        }

        let welcomePageProps = {
            state: this.state,
            handleShowSpecificPage: this.handleShowSpecificPage
        };

        let reviewPageProps = {
            state: this.state,
            handleShowSpecificPage: this.handleShowSpecificPage,
            handleSuccessSubmitFormData: this.handleSuccessSubmitFormData,
            handleShowErrorPage: this.handleShowErrorPage
        };

        let formPagesProps = {
            state: this.state,
            handleShowSpecificPageWithDataClick: this.handleShowSpecificPageWithDataClick,
            handleSuccessSubmitFormData: this.handleSuccessSubmitFormData,
            handleShowErrorPage: this.handleShowErrorPage
        };

        let errorPageProps = {
            state: this.state,
            handleShowSpecificPageWithDataClick: this.handleShowSpecificPageWithDataClick,
        };

        let thankyouPageProps = {
            state: this.state
        };

        return (
            <AppContext.Provider value={{workflow: this.state.app.schemas?.uiSchema || undefined}}>
                <Helmet>
                    <title>{this.state.app.name}</title>
                    <meta name="description" content={this.state.app.description} />
                    <meta property="og:title" content={this.state.app.name} />
                    <meta property="og:description" content={this.state.app.description} />
                    <meta name="version" content={process.env.REACT_APP_VERSION || '1.0.0'} />
                    { this.state.app.logo !== undefined && this.state.app.logo !== "" &&
                        <meta property="og:image" content={this.state.app.logo} />
                    }
                    { this.state.app.logo !== undefined && this.state.app.logo !== "" &&
                        <meta property="og:url" content={this.state.app.logo} />
                    }
                    <meta name="twitter:title" content={this.state.app.name} />
                    <meta name="twitter:description" content={this.state.app.description} />
                    { this.state.app.logo !== undefined && this.state.app.logo !== "" &&
                        <meta name="twitter:image" content={this.state.app.logo} />
                    }
                    { this.state.app.logo !== undefined && this.state.app.logo !== "" &&
                        <meta name="twitter:card" content={this.state.app.logo} />
                    }
                    <meta property="og:type" content="website" />
                    <meta property="og:locale" content={navigator.language.toLocaleLowerCase()} />
                    <meta name="robots" content="noindex,nofollow"/>
                </Helmet>
                <Fragment>
                    <ThemeProvider theme={this.getTheme()}>
                        <CssBaseline />

                        {this.state.pageView === PageView.Welcome &&
                            <Welcome {...welcomePageProps} />
                        }

                        {this.state.pageView === PageView.FormPages &&
                            <FormPages {...formPagesProps} key={this.state.page} />
                        }

                        {this.state.pageView === PageView.Review &&
                            <Review {...reviewPageProps} />
                        }

                        {this.state.pageView === PageView.Error &&
                            <Error {...errorPageProps} />
                        }

                        {this.state.pageView === PageView.ThankYou &&
                            <ThankYou {...thankyouPageProps} />
                        }
                    </ThemeProvider>
                </Fragment>
            </AppContext.Provider>
        );
    }

    private static setBrowserLocale() {
        let locale = navigator.language.toLocaleLowerCase();

        try {
            require("moment/locale/"+locale);
        } catch (e) {
            locale = moment.locale();
        }

        moment().locale(locale);
    }

    private loadPaypalSdkScript(config: any, newAppState: AppState) {
        const clientId = config.payPalAppClientId;
        const currency = config.currency;
        const script = document.createElement("script");
        const errorMessage = config.messages.paymentConfigErrorMessage;

        let src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}`;

        if (config.disableFundings.length) {
            src += '&disable-funding=' + config.disableFundings.join();
        }

        script.src = src;
        script.async = false;
        script.onerror = () => {
            this.setState({pageView: PageView.Error, message: errorMessage});
        };
        script.onload = () => {
            this.setState(newAppState);
        };

        document.body.appendChild(script);
    }
}

export default PowformApp;
